import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '../contexts/NotificationContext'
import { getFlaggedContentLog, markFlaggedAsResolved } from '../utils/flaggedWords'
import { isValidUrl } from '../utils/validation'
import ContentUploadSection from '../components/ContentUploadSection'
import ResourcesModal from '../components/ResourcesModal'
import './AdminPanel.css'

interface FlaggedContent {
  id: string
  contentId: string
  content: string
  authorId: string
  authorUsername: string
  flaggedWords: string[]
  timestamp: string
  resolved: boolean
}

const AdminPanel = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { addNotification } = useNotifications()
  const [activeTab, setActiveTab] = useState<'flagged' | 'featured' | 'newsletter' | 'analytics' | 'content' | 'schedule' | 'tutorials'>('flagged')
  const [tutorials, setTutorials] = useState<any[]>([])
  const [openResourcesModal, setOpenResourcesModal] = useState<string | null>(null)
  const [scheduleItems, setScheduleItems] = useState<any[]>([])
  const [editingSchedule, setEditingSchedule] = useState<string | null>(null)
  const [scheduleForm, setScheduleForm] = useState({ nextSessionUrl: '', nextSessionDate: '', nextSessionText: '' })
  const [flaggedContent, setFlaggedContent] = useState<FlaggedContent[]>([])
  const [posts, setPosts] = useState<any[]>([])
  const [selectedFeatured, setSelectedFeatured] = useState<string[]>([])
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterSummary, setNewsletterSummary] = useState('')
  const [actionItems, setActionItems] = useState<string[]>([])
  const [trends, setTrends] = useState<string[]>([])
  const [aiGenerating, _setAiGenerating] = useState(false)

  useEffect(() => {
    if (!user || !user.isAdmin) {
      navigate('/')
      return
    }
  }, [user, navigate])

  const loadTutorials = () => {
    const stored = localStorage.getItem('tutorials')
    if (stored) {
      try {
        setTutorials(JSON.parse(stored))
      } catch (e) {
        console.error('Error loading tutorials:', e)
      }
    }
  }

  const handleResourcesUpdate = (tutorialId: string, resources: any[]) => {
    const updated = tutorials.map(t =>
      t.id === tutorialId ? { ...t, resources } : t
    )
    setTutorials(updated)
    localStorage.setItem('tutorials', JSON.stringify(updated))
  }

  useEffect(() => {
    loadFlaggedContent()
    loadPosts()
    loadTutorials()
    const stored = localStorage.getItem('schedule_items')
    if (stored) {
      try {
        setScheduleItems(JSON.parse(stored))
      } catch (e) {
        console.error('Error loading schedule:', e)
      }
    } else {
      // Initialize with default schedule items
      const defaultSchedule = [
        {
          id: 'garden-streams',
          title: 'Garden Streams',
          location: 'Twitch Live',
          description: 'Weekly live coding sessions in the garden. Ambient explorations, plant data sonification, and chill vibes.',
          nextSessionUrl: '',
          nextSessionDate: '',
        },
        {
          id: 'creative-tech-lab',
          title: 'Creative Tech Lab',
          location: 'Hybrid',
          description: 'Experimental workshops combining code, sound, and interactive installations. Open to all skill levels.',
          nextSessionUrl: '',
          nextSessionDate: '',
        },
        {
          id: 'circuits-vines',
          title: 'Circuits & Vines',
          location: 'Online',
          description: 'Hybrid sets blending live coding with hardware jams. Motion sensors, MIDI controllers, and code.',
          nextSessionUrl: '',
          nextSessionDate: '',
        },
      ]
      setScheduleItems(defaultSchedule)
      localStorage.setItem('schedule_items', JSON.stringify(defaultSchedule))
    }
  }, [])

  const handleScheduleUpdate = (itemId: string) => {
    const updated = scheduleItems.map(item =>
      item.id === itemId
        ? { ...item, nextSessionUrl: scheduleForm.nextSessionUrl, nextSessionDate: scheduleForm.nextSessionDate, nextSessionText: scheduleForm.nextSessionText }
        : item
    )
    setScheduleItems(updated)
    localStorage.setItem('schedule_items', JSON.stringify(updated))
    setEditingSchedule(null)
    setScheduleForm({ nextSessionUrl: '', nextSessionDate: '', nextSessionText: '' })
  }

  const loadFlaggedContent = () => {
    const logs = getFlaggedContentLog()
    setFlaggedContent(logs.filter((log: FlaggedContent) => !log.resolved))
  }

  const loadPosts = () => {
    const stored = localStorage.getItem('forum_posts')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setPosts(parsed)
        const featured = parsed.filter((p: any) => p.isFeatured).map((p: any) => p.id)
        setSelectedFeatured(featured)
      } catch (e) {
        console.error('Error loading posts:', e)
      }
    }
  }

  const handleDeleteFlagged = (logId: string, contentId: string, authorId: string) => {
    if (window.confirm('Delete this flagged content?')) {
      // Delete the actual content
      const stored = localStorage.getItem('forum_posts')
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          const updated = parsed.filter((p: any) => p.id !== contentId)
          localStorage.setItem('forum_posts', JSON.stringify(updated))
          loadPosts()
        } catch (e) {
          console.error('Error deleting post:', e)
        }
      }

      // Mark as resolved
      markFlaggedAsResolved(logId)
      loadFlaggedContent()

      // Notify user
      addNotification({
        type: 'content_removed',
        title: 'Content Removed',
        message: 'Your content was removed for using flagged words.',
        userId: authorId,
      })
    }
  }

  const handleResolveFlagged = (logId: string) => {
    markFlaggedAsResolved(logId)
    loadFlaggedContent()
  }

  const handleToggleFeatured = (postId: string) => {
    const newFeatured = selectedFeatured.includes(postId)
      ? selectedFeatured.filter(id => id !== postId)
      : [...selectedFeatured, postId]

    setSelectedFeatured(newFeatured)

    // Update posts
    const stored = localStorage.getItem('forum_posts')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        const updated = parsed.map((p: any) => ({
          ...p,
          isFeatured: newFeatured.includes(p.id),
        }))
        localStorage.setItem('forum_posts', JSON.stringify(updated))
        loadPosts()
      } catch (e) {
        console.error('Error updating featured posts:', e)
      }
    }
  }

  const handleNewsletterParse = async () => {
    if (!newsletterEmail.trim()) return

    // Simulate AI parsing (in production, this would call an AI API)
    setNewsletterSummary('Parsing newsletter...')
    await new Promise(resolve => setTimeout(resolve, 2000))

    // Mock AI parsing results
    const mockSummary = `Weekly Summary:
- 15 new posts this week
- 42 comments across all posts
- Top trending topic: "Sonic Pi Sessions"
- Most active user: testuser
- 3 flagged posts resolved

Key Highlights:
- Community engagement increased by 25%
- New tutorial requests for Strudel
- Several users sharing their first creations`

    const mockActionItems = [
      'Review and respond to tutorial requests',
      'Feature top community creations',
      'Schedule next live coding session',
      'Update documentation based on user questions',
    ]

    const mockTrends = [
      'Growing interest in algorithmic composition',
      'Increased questions about hardware integration',
      'More users sharing code snippets',
      'Community requesting more video tutorials',
    ]

    setNewsletterSummary(mockSummary)
    setActionItems(mockActionItems)
    setTrends(mockTrends)
  }

  const handlePublishNewsletter = () => {
    // Notify all users
    let allUsers: string[] = []
    try {
      allUsers = JSON.parse(localStorage.getItem('all_users') || '[]')
    } catch (e) {
      console.error('Error reading all_users:', e)
    }
    allUsers.forEach((userId: string) => {
      addNotification({
        type: 'weekly_update',
        title: 'Weekly Newsletter',
        message: 'New weekly community update available!',
        userId,
      })
    })

    alert('Newsletter published! All users have been notified.')
    setNewsletterEmail('')
    setNewsletterSummary('')
    setActionItems([])
    setTrends([])
  }

  // Calculate analytics
  const analytics = {
    totalPosts: posts.length,
    totalComments: posts.reduce((sum, p) => sum + (p.comments?.length || 0), 0),
    totalLikes: posts.reduce((sum, p) => sum + (p.likes?.length || 0), 0),
    flaggedCount: flaggedContent.length,
    featuredCount: selectedFeatured.length,
    activeUsers: new Set(posts.map((p: any) => p.authorId).concat(
      posts.flatMap((p: any) => p.comments?.map((c: any) => c.authorId) || [])
    )).size,
  }

  if (!user || !user.isAdmin) {
    return null
  }

  return (
    <main className="main-content admin-panel">
      <div className="admin-container">
        <h1 className="admin-title">admin panel</h1>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'flagged' ? 'active' : ''}`}
            onClick={() => setActiveTab('flagged')}
          >
            <i className="fas fa-flag"></i>
            <span>flagged content</span>
            {flaggedContent.length > 0 && (
              <span className="admin-tab-badge">{flaggedContent.length}</span>
            )}
          </button>
          <button
            className={`admin-tab ${activeTab === 'featured' ? 'active' : ''}`}
            onClick={() => setActiveTab('featured')}
          >
            <i className="fas fa-star"></i>
            <span>featured posts</span>
          </button>
          <button
            className={`admin-tab ${activeTab === 'newsletter' ? 'active' : ''}`}
            onClick={() => setActiveTab('newsletter')}
          >
            <i className="fas fa-envelope"></i>
            <span>newsletter</span>
          </button>
          <button
            className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <i className="fas fa-chart-line"></i>
            <span>analytics</span>
          </button>
          <button
            className={`admin-tab ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <i className="fas fa-upload"></i>
            <span>content upload</span>
          </button>
          <button
            className={`admin-tab ${activeTab === 'tutorials' ? 'active' : ''}`}
            onClick={() => setActiveTab('tutorials')}
          >
            <i className="fas fa-book"></i>
            <span>tutorials</span>
          </button>
          <button
            className={`admin-tab ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <i className="fas fa-calendar-alt"></i>
            <span>schedule</span>
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'flagged' && (
            <div className="admin-section">
              <h2 className="admin-section-title">flagged content log</h2>
              {flaggedContent.length === 0 ? (
                <div className="admin-empty">
                  <p>no flagged content</p>
                </div>
              ) : (
                <div className="flagged-list">
                  {flaggedContent.map((item) => (
                    <div key={item.id} className="flagged-item">
                      <div className="flagged-item-header">
                        <div className="flagged-item-info">
                          <div className="flagged-item-author">@{item.authorUsername}</div>
                          <div className="flagged-item-time">
                            {new Date(item.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <div className="flagged-item-words">
                          {item.flaggedWords.map((word, idx) => (
                            <span key={idx} className="flagged-word-badge">{word}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flagged-item-content">{item.content.substring(0, 200)}...</div>
                      <div className="flagged-item-actions">
                        <button
                          className="admin-button admin-button-danger"
                          onClick={() => handleDeleteFlagged(item.id, item.contentId, item.authorId)}
                        >
                          <i className="fas fa-trash"></i>
                          delete
                        </button>
                        <button
                          className="admin-button admin-button-secondary"
                          onClick={() => handleResolveFlagged(item.id)}
                        >
                          <i className="fas fa-check"></i>
                          resolve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'featured' && (
            <div className="admin-section">
              <h2 className="admin-section-title">featured posts</h2>
              <p className="admin-section-description">
                Select posts to feature weekly. Featured posts appear at the top of the forum.
              </p>
              <div className="featured-posts-list">
                {posts.length === 0 ? (
                  <div className="admin-empty">
                    <p>no posts available</p>
                  </div>
                ) : (
                  posts.map((post) => (
                    <div key={post.id} className="featured-post-item">
                      <label className="featured-post-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedFeatured.includes(post.id)}
                          onChange={() => handleToggleFeatured(post.id)}
                        />
                        <div className="featured-post-content">
                          <div className="featured-post-title">{post.title}</div>
                          <div className="featured-post-meta">
                            by @{post.authorUsername} • {new Date(post.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'newsletter' && (
            <div className="admin-section">
              <h2 className="admin-section-title">weekly newsletter</h2>
              <p className="admin-section-description">
                Paste your weekly newsletter email and AI will parse it to create a summary, action items, and trend analysis.
              </p>
              <div className="newsletter-form">
                <textarea
                  className="newsletter-input"
                  placeholder="Paste your weekly newsletter email here..."
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  rows={10}
                />
                <button
                  className="admin-button admin-button-primary"
                  onClick={handleNewsletterParse}
                  disabled={!newsletterEmail.trim() || aiGenerating}
                >
                  <i className="fas fa-magic"></i>
                  {aiGenerating ? 'generating...' : 'generate from community data'}
                </button>
                <p className="newsletter-hint">
                  Leave email field empty to auto-generate from current community activity, or paste custom content to parse.
                </p>
              </div>

              {newsletterSummary && (
                <div className="newsletter-results">
                  <div className="newsletter-summary">
                    <h3>summary</h3>
                    <pre>{newsletterSummary}</pre>
                  </div>
                  {actionItems.length > 0 && (
                    <div className="newsletter-action-items">
                      <h3>action items</h3>
                      <ul>
                        {actionItems.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {trends.length > 0 && (
                    <div className="newsletter-trends">
                      <h3>trend analysis</h3>
                      <ul>
                        {trends.map((trend, idx) => (
                          <li key={idx}>{trend}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button
                    className="admin-button admin-button-primary"
                    onClick={handlePublishNewsletter}
                  >
                    <i className="fas fa-paper-plane"></i>
                    publish newsletter
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="admin-section">
              <h2 className="admin-section-title">community analytics</h2>
              <div className="analytics-grid">
                <div className="analytics-card">
                  <div className="analytics-card-value">{analytics.totalPosts}</div>
                  <div className="analytics-card-label">total posts</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-value">{analytics.totalComments}</div>
                  <div className="analytics-card-label">total comments</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-value">{analytics.totalLikes}</div>
                  <div className="analytics-card-label">total likes</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-value">{analytics.activeUsers}</div>
                  <div className="analytics-card-label">active users</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-value">{analytics.flaggedCount}</div>
                  <div className="analytics-card-label">flagged items</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-value">{analytics.featuredCount}</div>
                  <div className="analytics-card-label">featured posts</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <ContentUploadSection />
          )}

          {activeTab === 'tutorials' && (
            <div className="admin-section">
              <h2 className="admin-section-title">manage tutorials</h2>
              <p className="admin-section-description">
                View and manage all tutorials. Click on a tutorial to edit its resources.
              </p>
              <div className="admin-tutorials-list">
                {tutorials.length === 0 ? (
                  <div className="admin-empty">
                    <p>no tutorials found</p>
                  </div>
                ) : (
                  tutorials.map((tutorial) => (
                    <div key={tutorial.id} className="admin-tutorial-item">
                      <div className="admin-tutorial-header">
                        <h3>{tutorial.title}</h3>
                        <div className="admin-tutorial-meta">
                          <span className="admin-tutorial-category">{tutorial.category}</span>
                          <span className={`admin-tutorial-difficulty ${tutorial.difficulty}`}>
                            {tutorial.difficulty}
                          </span>
                          <span className="admin-tutorial-duration">{tutorial.duration}</span>
                        </div>
                      </div>
                      <p className="admin-tutorial-description">{tutorial.description}</p>
                      <div className="admin-tutorial-actions">
                        <button
                          className="admin-button admin-button-secondary"
                          onClick={() => setOpenResourcesModal(tutorial.id)}
                        >
                          <i className="fas fa-folder-open"></i>
                          manage resources ({tutorial.resources?.length || 0})
                        </button>
                      </div>
                      {openResourcesModal === tutorial.id && (
                        <ResourcesModal
                          tutorialId={tutorial.id}
                          isOpen={true}
                          onClose={() => setOpenResourcesModal(null)}
                          resources={tutorial.resources || []}
                          onResourcesUpdate={(resources) => handleResourcesUpdate(tutorial.id, resources)}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="admin-section">
              <h2 className="admin-section-title">manage schedule</h2>
              <p className="admin-section-description">
                Update next session information for each schedule category. Add live links for upcoming streams/releases.
              </p>
              <div className="schedule-admin-list">
                {scheduleItems.length === 0 ? (
                  <div className="admin-empty">
                    <p>no schedule items found</p>
                  </div>
                ) : (
                  scheduleItems.map((item) => (
                    <div key={item.id} className="schedule-admin-item">
                      <div className="schedule-admin-header">
                        <h3>{item.title}</h3>
                        <div className="schedule-admin-location">{item.location}</div>
                      </div>
                      <p className="schedule-admin-description">{item.description}</p>
                      {editingSchedule === item.id ? (
                        <div className="schedule-admin-form">
                          <input
                            type="date"
                            className="admin-input"
                            value={scheduleForm.nextSessionDate}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, nextSessionDate: e.target.value })}
                          />
                          <input
                            type="url"
                            className="admin-input"
                            placeholder="live link (Twitch, YouTube, etc.)"
                            value={scheduleForm.nextSessionUrl}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, nextSessionUrl: e.target.value })}
                          />
                          <input
                            type="text"
                            className="admin-input"
                            placeholder="link text (e.g., watch live, view recording)"
                            value={scheduleForm.nextSessionText || ''}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, nextSessionText: e.target.value })}
                          />
                          <div className="schedule-admin-actions">
                            <button
                              className="admin-button admin-button-primary"
                              onClick={() => handleScheduleUpdate(item.id)}
                            >
                              save
                            </button>
                            <button
                              className="admin-button admin-button-secondary"
                              onClick={() => {
                                setEditingSchedule(null)
                                setScheduleForm({ nextSessionUrl: '', nextSessionDate: '', nextSessionText: '' })
                              }}
                            >
                              cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="schedule-admin-display">
                          {item.nextSessionUrl ? (
                            <div className="schedule-admin-next">
                              <div className="schedule-admin-next-info">
                                {item.nextSessionDate && (
                                  <div className="schedule-admin-next-date">{item.nextSessionDate}</div>
                                )}
                                {isValidUrl(item.nextSessionUrl) && (
                                  <a href={item.nextSessionUrl} target="_blank" rel="noopener noreferrer" className="schedule-admin-next-link">
                                    <i className="fas fa-external-link-alt"></i>
                                    {item.nextSessionText || item.nextSessionUrl}
                                  </a>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="schedule-admin-empty">no next session set</div>
                          )}
                          <button
                            className="admin-button admin-button-secondary"
                            onClick={() => {
                              setEditingSchedule(item.id)
                              setScheduleForm({
                                nextSessionUrl: item.nextSessionUrl || '',
                                nextSessionDate: item.nextSessionDate || '',
                                nextSessionText: item.nextSessionText || '',
                              })
                            }}
                          >
                            <i className="fas fa-edit"></i>
                            {item.nextSessionUrl ? 'edit' : 'add next session'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default AdminPanel

